import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { useAuthStore } from '../store/auth';

function useIsSystemAdmin() {
  const { data } = useQuery({
    queryKey: ['admin', 'my-permissions'],
    queryFn: () => adminApi.getMyPermissions(),
    staleTime: 5 * 60 * 1000,
  });
  return (data?.data ?? []).includes('admin-management');
}

type AdminUser = {
  id: string;
  name: string;
  phone: string;
  createdAt: string;
  email: string | null;
  loginId: string | null;
  role?: { id: string; code: string; name: string } | null;
};

type Invitation = {
  id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
  role: { id: string; name: string } | null;
  invitedBy: { id: string; name: string };
};

export default function Admins() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [activeTab, setActiveTab] = useState<'admins' | 'invitations'>('admins');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => adminApi.getAdmins(),
  });

  const { data: invitationsData } = useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => adminApi.getInvitations(),
    enabled: activeTab === 'invitations',
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => adminApi.cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const admins: AdminUser[] = data.data || [];
  const invitations: Invitation[] = invitationsData?.data || [];
  const pendingInvitations = invitations.filter(
    (inv) => !inv.usedAt && new Date(inv.expiresAt) > new Date(),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">슈퍼관리자 관리</h2>
        <Button variant="primary" onClick={() => setShowInviteModal(true)}>
          <span>+</span>
          <span>초대하기</span>
        </Button>
      </div>

      {/* 탭 */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('admins')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'admins'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          관리자 목록
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
            activeTab === 'invitations'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          대기 중인 초대
          {pendingInvitations.length > 0 && activeTab !== 'invitations' && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {pendingInvitations.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'admins' && (
        <>
          <Card padding="none" radius="lg" className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계정 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    역할
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setDetailTarget(admin)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{admin.name}</span>
                        {admin.id === currentUser?.id && (
                          <span className="px-2 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">
                            나
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {admin.email ? (
                        admin.email
                      ) : admin.loginId ? (
                        <span className="flex items-center gap-1">
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">ID</span>
                          {admin.loginId}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {admin.role ? (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          {admin.role.name}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {new Date(admin.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="mt-4 text-sm text-gray-500">총 {admins.length}명의 슈퍼관리자</p>
        </>
      )}

      {activeTab === 'invitations' && (
        <Card padding="none" radius="lg" className="overflow-hidden">
          {invitations.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">대기 중인 초대가 없습니다.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    역할
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    만료일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    초대자
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((inv) => {
                  const isExpired = new Date(inv.expiresAt) < new Date();
                  const statusLabel = inv.usedAt
                    ? '사용됨'
                    : isExpired
                      ? '만료됨'
                      : '대기 중';
                  const statusClass = inv.usedAt
                    ? 'bg-green-100 text-green-700'
                    : isExpired
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-yellow-100 text-yellow-700';
                  const canCancel = !inv.usedAt && !isExpired && true;
                  return (
                    <tr key={inv.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {inv.email ? (
                          <span className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">이메일</span>
                            {inv.email}
                          </span>
                        ) : inv.phone ? (
                          <span className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">SMS</span>
                            {inv.phone}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {inv.name ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {inv.role ? (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                            {inv.role.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-xs rounded ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {new Date(inv.expiresAt).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {inv.invitedBy.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {canCancel && (
                          <button
                            onClick={() => cancelInvitationMutation.mutate(inv.id)}
                            disabled={cancelInvitationMutation.isPending}
                            className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                          >
                            취소
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* 초대 모달 */}
      {showInviteModal && (
        <InviteAdminModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'invitations'] });
            setShowInviteModal(false);
            setActiveTab('invitations');
          }}
        />
      )}

      {/* 관리자 상세/수정 모달 */}
      {detailTarget && (
        <AdminDetailModal
          admin={detailTarget}
          isCurrentUser={detailTarget.id === currentUser?.id}
          onClose={() => setDetailTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
            setDetailTarget(null);
          }}
        />
      )}
    </div>
  );
}

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function InviteAdminModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState('');
  const [invitationUrl, setInvitationUrl] = useState('');
  const [sentStatus, setSentStatus] = useState<{ emailSent: boolean; smsSent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const formId = useId();

  const { data: rolesData } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminApi.getRoles(),
  });
  const roles = rolesData?.data ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.createInvitation({
        email: channel === 'email' ? email : undefined,
        phone: channel === 'sms' ? phone : undefined,
        name: name || undefined,
        roleId: roleId || undefined,
      }),
    onSuccess: (result) => {
      if (result.success && result.data) {
        setInvitationUrl(result.data.invitationUrl);
        setSentStatus({ emailSent: result.data.emailSent, smsSent: result.data.smsSent });
      } else {
        setError(result.error?.message || '초대 생성에 실패했습니다.');
      }
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : '초대 생성 중 오류가 발생했습니다.';
      setError(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (channel === 'email' && !email) {
      setError('이메일을 입력해주세요.');
      return;
    }
    if (channel === 'sms' && !phone) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    mutation.mutate();
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(invitationUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getSentMessage = () => {
    if (channel === 'email') {
      return sentStatus?.emailSent
        ? { color: 'text-green-600', text: '초대 이메일이 발송되었습니다.' }
        : { color: 'text-amber-600', text: '이메일 발송 실패 — 아래 링크를 직접 전달해 주세요.' };
    }
    return sentStatus?.smsSent
      ? { color: 'text-green-600', text: '초대 SMS가 발송되었습니다.' }
      : { color: 'text-amber-600', text: 'SMS 발송 실패 — 아래 링크를 직접 전달해 주세요.' };
  };

  if (invitationUrl) {
    const sentMsg = getSentMessage();
    return (
      // 종전처럼 어느 닫기든 onSuccess(목록 갱신)로 간다.
      <Modal
        open
        onClose={onSuccess}
        title="초대 링크 생성 완료"
        size="md"
        footer={
          <Button variant="secondary" onClick={onSuccess}>
            닫기
          </Button>
        }
      >
          <p className={`text-sm mb-4 ${sentMsg.color}`}>
            {sentMsg.text} 링크는 7일간 유효합니다.
          </p>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={invitationUrl}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 text-gray-700"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className="whitespace-nowrap"
            >
              {copied ? '복사됨!' : '복사'}
            </Button>
          </div>
      </Modal>
    );
  }

  // 미저장 닫기 보호 — 생성 폼이라 기준선은 빈 폼이다. 채널 탭 전환만으로는 적은 내용이 없으니 dirty가 아니다.
  // (생성 완료 화면은 위에서 따로 반환하고 isDirty를 걸지 않는다 — 이미 저장된 상태다.)
  const isDirty = email.length > 0 || phone.length > 0 || name.length > 0 || roleId.length > 0;

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title="슈퍼관리자 초대"
      size="md"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '생성 중...' : '초대 링크 생성'}
          </Button>
        </>
      }
    >
        {/* 채널 탭 */}
        <div className="flex border border-gray-200 rounded-control mb-4 overflow-hidden">
          <button
            type="button"
            onClick={() => setChannel('email')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              channel === 'email'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            이메일로 초대
          </button>
          <button
            type="button"
            onClick={() => setChannel('sms')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              channel === 'sms'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            SMS로 초대
          </button>
        </div>

        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          {channel === 'email' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                전화번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="01012345678"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이름 (선택)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="미입력 시 등록 시 직접 입력"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              역할 (선택)
            </label>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">역할 없음</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name} ({role.code})
                </option>
              ))}
            </select>
          </div>
        </form>
    </Modal>
  );
}

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function AdminDetailModal({
  admin,
  isCurrentUser,
  onClose,
  onSuccess,
}: {
  admin: AdminUser;
  isCurrentUser: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isSystemAdmin = useIsSystemAdmin();

  const [selectedRoleId, setSelectedRoleId] = useState<string>(admin.role?.id ?? '');
  const [editName, setEditName] = useState(admin.name);
  const [editPhone, setEditPhone] = useState(admin.phone);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  const { data: rolesData } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminApi.getRoles(),
  });

  const roles = rolesData?.data ?? [];

  const infoMutation = useMutation({
    mutationFn: () => adminApi.updateAdmin(admin.id, {
      name: editName.trim() || undefined,
      phone: editPhone.trim() || undefined,
    }),
    onSuccess: (result) => {
      if (result.success) {
        setSuccessMsg('정보가 수정되었습니다.');
        setError('');
        onSuccess();
      } else {
        setError(result.error?.message || '수정에 실패했습니다.');
      }
    },
    onError: () => {
      setError('수정 중 오류가 발생했습니다.');
    },
  });

  const roleMutation = useMutation({
    mutationFn: () => adminApi.assignAdminRole(admin.id, selectedRoleId || null),
    onSuccess: (result) => {
      if (result.success) {
        setSuccessMsg('역할이 변경되었습니다.');
        setError('');
        onSuccess();
      } else {
        setError(result.error?.message || '역할 변경에 실패했습니다.');
      }
    },
    onError: () => {
      setError('역할 변경 중 오류가 발생했습니다.');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () => adminApi.resetAdminPassword(admin.id),
    onSuccess: (result) => {
      if (result.success && result.data) {
        setTempPassword(result.data.tempPassword);
        setError('');
      } else {
        setError(result.error?.message || '비밀번호 초기화에 실패했습니다.');
      }
    },
    onError: () => {
      setError('비밀번호 초기화 중 오류가 발생했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.removeAdmin(admin.id),
    onSuccess: () => {
      onSuccess();
    },
    onError: () => {
      setError('권한 제거에 실패했습니다.');
    },
  });

  const handleInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    if (!editName.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    infoMutation.mutate();
  };

  const handleRoleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    roleMutation.mutate();
  };

  if (showDeleteConfirm) {
    return (
      // 종전처럼 어느 닫기든 상세로 돌아간다(상세 자체를 닫지 않는다).
      <Modal
        open
        onClose={() => setShowDeleteConfirm(false)}
        title="슈퍼관리자 권한 제거"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              취소
            </button>
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '처리 중...' : '권한 제거'}
            </Button>
          </>
        }
      >
          <p className="text-gray-600">
            <span className="font-medium">{admin.name}</span>의 슈퍼관리자 권한을 제거하시겠습니까?
          </p>
          {deleteMutation.isError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              권한 제거에 실패했습니다.
            </div>
          )}
      </Modal>
    );
  }

  if (tempPassword) {
    return (
      <Modal
        open
        onClose={onClose}
        title="임시 비밀번호 발급 완료"
        size="md"
        footer={
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
        }
      >
          <p className="text-sm text-gray-600 mb-4">
            아래 임시 비밀번호를 관리자에게 전달하세요. 로그인 후 즉시 변경하도록 안내해 주세요.
          </p>
          <div className="p-3 bg-gray-50 border border-gray-200 rounded font-mono text-lg font-bold text-center tracking-widest">
            {tempPassword}
          </div>
      </Modal>
    );
  }

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식). 두 폼 중 어느 쪽이든 저장하면
  // 부모 onSuccess가 창을 닫으므로 「저장 뒤 기준선 갱신」은 필요 없다.
  const isDirty =
    editName !== admin.name || editPhone !== admin.phone || selectedRoleId !== (admin.role?.id ?? '');

  return (
    // 정보 수정·역할 변경 폼이 본문에 둘 있어 하단 버튼 줄은 base footer로 옮긴다(폼 제출과 무관한 버튼뿐이다).
    <Modal
      open
      onClose={onClose}
      isDirty={isDirty}
      title={
        <span className="flex items-center gap-2">
          관리자 상세
          {isCurrentUser && (
            <span className="px-2 py-0.5 text-xs font-normal bg-brand-100 text-brand-700 rounded">나</span>
          )}
        </span>
      }
      ariaLabel="관리자 상세"
      size="md"
      footer={
        <div className="flex w-full justify-between">
          <div>
            {isSystemAdmin && !isCurrentUser && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-red-600 hover:text-red-800 text-sm border border-red-200 rounded hover:bg-red-50"
              >
                권한 제거
              </button>
            )}
          </div>
          <ModalCancelButton onCancel={onClose} label="닫기" />
        </div>
      }
    >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            {successMsg}
          </div>
        )}

        {/* 기본 정보 수정 */}
        <form onSubmit={handleInfoSubmit} className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <label className="w-20 text-sm text-gray-500 shrink-0">이름</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="w-20 text-sm text-gray-500 shrink-0">전화번호</label>
            <input
              type="tel"
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
              placeholder="전화번호"
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center">
            <span className="w-20 text-sm text-gray-500 shrink-0">계정 ID</span>
            <span className="text-sm text-gray-900">
              {admin.email ? (
                admin.email
              ) : admin.loginId ? (
                <span className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">ID</span>
                  {admin.loginId}
                </span>
              ) : '-'}
            </span>
          </div>
          <div className="flex items-center">
            <span className="w-20 text-sm text-gray-500 shrink-0">등록일</span>
            <span className="text-sm text-gray-900">
              {new Date(admin.createdAt).toLocaleDateString('ko-KR')}
            </span>
          </div>
          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={infoMutation.isPending}
            >
              {infoMutation.isPending ? '저장 중...' : '정보 저장'}
            </Button>
          </div>
        </form>

        <hr className="border-gray-200 mb-6" />

        {/* 역할 변경 (SYSTEM_ADMIN만) */}
        {isSystemAdmin && (
          <>
            <form onSubmit={handleRoleSubmit} className="space-y-3 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">역할</label>
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">역할 없음</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name} ({role.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={roleMutation.isPending}
                >
                  {roleMutation.isPending ? '처리 중...' : '역할 저장'}
                </Button>
              </div>
            </form>

            <hr className="border-gray-200 mb-6" />
          </>
        )}

        {/* 비밀번호 초기화 (SYSTEM_ADMIN이 타인에게만) */}
        {isSystemAdmin && !isCurrentUser && (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">비밀번호 초기화</p>
                <p className="text-xs text-gray-400 mt-0.5">임시 비밀번호를 발급하고 기존 세션을 만료합니다</p>
              </div>
              <button
                type="button"
                onClick={() => resetPasswordMutation.mutate()}
                disabled={resetPasswordMutation.isPending}
                className="px-3 py-1.5 text-sm text-amber-700 border border-amber-300 rounded hover:bg-amber-50 disabled:opacity-50"
              >
                {resetPasswordMutation.isPending ? '처리 중...' : '초기화'}
              </button>
            </div>
            <hr className="border-gray-200 mt-6" />
          </div>
        )}
    </Modal>
  );
}
